import { Button } from '@heroui/react'
import { Info as InfoIcon } from 'lucide-react'
import { type ReactNode, useState } from 'react'

type CardHeadProps = {
  title: string
  badge?: ReactNode
  info?: (string | null | false | undefined)[]
}

/**
 * A block title, optionally with a badge and an info toggle. Explanation goes
 * behind the toggle, not above the chart, and opens on click, never on hover:
 * a hover tooltip cannot be screenshotted and does not exist on a phone.
 * The panel is a sibling below the head so it opens across the full card.
 */
const CardHead = ({ title, badge, info = [] }: CardHeadProps) => {
  const [open, setOpen] = useState(false)
  const lines = info.filter((line): line is string => Boolean(line))

  return (
    <>
      <div className="flex min-w-0 items-center gap-2">
        <h3 className="min-w-0 flex-1 text-[0.9375rem] leading-snug font-semibold tracking-tight">
          {title}
        </h3>
        {badge}
        {lines.length > 0 && (
          <Button
            isIconOnly
            size="sm"
            variant={open ? 'secondary' : 'ghost'}
            aria-label="About this block"
            aria-expanded={open}
            onPress={() => setOpen(prev => !prev)}
          >
            <InfoIcon size={16} />
          </Button>
        )}
      </div>
      {open && (
        <div className="bg-surface-secondary grid gap-2 rounded-lg px-4 py-3">
          {lines.map((line, i) => (
            <p key={i} className="text-sm leading-relaxed">
              {line}
            </p>
          ))}
        </div>
      )}
    </>
  )
}

export default CardHead
